'use client';

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

// Direct imports instead of lazy loading to prevent webpack module resolution issues
import QuickActionsPanel from './QuickActionsPanel';
import ReadingProgressCard from './ReadingProgressCard';
import DevotionalPlanCard from './DevotionalPlanCard';
import PrayerJournalCard from './PrayerJournalCard';
import NotesHighlightsCard from './NotesHighlightsCard';
import GoalsAchievementsCard from './GoalsAchievementsCard';
import CalendarIntegrationCard from './CalendarIntegrationCard';
import RecommendationsCard from './RecommendationsCard';

export default function PersonalDashboardInteractive({ initialData, user, userProfile, onDataUpdate }) {
  const [dashboardData, setDashboardData] = useState(initialData);
  const [lastSyncTime, setLastSyncTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const router = useRouter();

  useEffect(() => {
    // Set initial times after component mounts
    const now = new Date();
    setLastSyncTime(now?.toLocaleTimeString?.() || '');
    setCurrentDate(now?.toLocaleDateString?.() || '');
    
    // Simulate periodic data sync
    const syncInterval = setInterval(() => {
      const updateTime = new Date();
      setLastSyncTime(updateTime?.toLocaleTimeString?.() || '');
    }, 60000); // Update every minute

    return () => clearInterval(syncInterval);
  }, []);

  const handleQuickAction = (actionId) => {
    console.log(`Quick action clicked: ${actionId}`);
    // Handle navigation or action based on actionId
    switch (actionId) {
      case 'read': router?.push('/digital-books-library');
        break;
      case 'pray':
        // Scroll to prayer journal section
        document?.getElementById('prayer-journal')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'listen': router?.push('/audio-book-library');
        break;
      case 'notes':
        // Scroll to notes section
        document?.getElementById('notes-highlights')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'search': router?.push('/bible-reader?search=true');
        break;
      case 'community': router?.push('/community-hub');
        break;
      default:
        break;
    }
  };

  const handleDevotionalClick = () => {
    // Navigate to specific devotional detail page if slug is available
    if (dashboardData?.devotionalPlan?.slug) {
      router?.push(`/devotionals/${dashboardData?.devotionalPlan?.slug}`);
    } else {
      // Fallback to main devotionals page
      router?.push('/devotionals');
    }
  };

  const handleDevotionalHistoryClick = () => {
    // Navigate to devotionals page with calendar view
    router?.push('/devotionals');
  };

  const handleBookmarkClick = (type, id) => {
    if (type === 'audiobook') {
      router?.push(`/single-audio-book-details?id=${id}`);
    } else if (type === 'book') {
      router?.push(`/advanced-e-book-reader?id=${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-crimson font-semibold text-foreground mb-2">
                Welcome back, {dashboardData?.user?.name || 'Guest'}
              </h1>
              <p className="text-muted-foreground font-inter">
                Continue your spiritual journey with personalized insights and resources
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-inter text-muted-foreground">Last synced</p>
              <p className="text-sm font-inter font-medium text-foreground">
                {lastSyncTime || 'Syncing...'}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <QuickActionsPanel onActionClick={handleQuickAction} />
        </div>

        {/* Bookmarks Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Audio Book Bookmarks */}
          <div className="bg-card rounded-xl p-6 border border-border hover-lift">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-crimson font-semibold text-foreground">
                📖 Audiobook Progress
              </h3>
              <Link 
                href="/audio-book-library" 
                className="text-sm font-inter text-primary hover:text-primary/80 contemplative-transition"
              >
                View All
              </Link>
            </div>
            <div className="space-y-3">
              {dashboardData?.bookmarks?.audiobooks?.slice(0, 3)?.map((audiobook) => (
                <div
                  key={audiobook?.id}
                  onClick={() => handleBookmarkClick('audiobook', audiobook?.id)}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-surface cursor-pointer contemplative-transition"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={audiobook?.image} 
                      alt={audiobook?.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-inter font-medium text-foreground truncate">
                      {audiobook?.title}
                    </p>
                    <p className="text-xs font-inter text-muted-foreground truncate">
                      {audiobook?.currentChapter}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex-1 bg-muted rounded-full h-1.5">
                        <div 
                          className="bg-primary rounded-full h-1.5 contemplative-transition"
                          style={{ width: `${audiobook?.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-inter text-muted-foreground">
                        {audiobook?.progress}%
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-inter text-muted-foreground">
                      {audiobook?.remainingTime} left
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Book Reading Bookmarks */}
          <div className="bg-card rounded-xl p-6 border border-border hover-lift">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-crimson font-semibold text-foreground">
                📚 Reading Progress
              </h3>
              <Link 
                href="/digital-books-library" 
                className="text-sm font-inter text-primary hover:text-primary/80 contemplative-transition"
              >
                View All
              </Link>
            </div>
            <div className="space-y-3">
              {dashboardData?.bookmarks?.books?.slice(0, 3)?.map((book) => (
                <div
                  key={book?.id}
                  onClick={() => handleBookmarkClick('book', book?.id)}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-surface cursor-pointer contemplative-transition"
                >
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={book?.image} 
                      alt={book?.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-inter font-medium text-foreground truncate">
                      {book?.title}
                    </p>
                    <p className="text-xs font-inter text-muted-foreground truncate">
                      {book?.currentChapter}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex-1 bg-muted rounded-full h-1.5">
                        <div 
                          className="bg-accent rounded-full h-1.5 contemplative-transition"
                          style={{ width: `${book?.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-inter text-muted-foreground">
                        {book?.progress}%
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex flex-col space-y-0.5">
                    <p className="text-xs font-inter text-muted-foreground">
                      {book?.highlights} highlights
                    </p>
                    <p className="text-xs font-inter text-muted-foreground">
                      {book?.notes} notes
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
          {/* Reading Progress */}
          <ReadingProgressCard readingStats={dashboardData?.readingStats} />
          
          {/* Devotional Plan */}
          <DevotionalPlanCard 
            devotionalPlan={dashboardData?.devotionalPlan} 
            onDevotionalClick={handleDevotionalClick}
            onHistoryClick={handleDevotionalHistoryClick}
          />
          
          {/* Prayer Journal */}
          <div id="prayer-journal">
            <PrayerJournalCard prayerEntries={dashboardData?.prayerEntries} />
          </div>
          
          {/* Notes & Highlights */}
          <div id="notes-highlights">
            <NotesHighlightsCard notes={dashboardData?.notes} />
          </div>
          
          {/* Goals & Achievements */}
          <GoalsAchievementsCard 
            goals={dashboardData?.goals} 
            achievements={dashboardData?.achievements} 
          />
          
          {/* Calendar Integration */}
          <CalendarIntegrationCard scheduledEvents={dashboardData?.scheduledEvents} />
        </div>

        {/* Recommendations Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecommendationsCard recommendations={dashboardData?.recommendations} />
          
          {/* Backup & Sync Status */}
          <div className="bg-card rounded-xl p-6 border border-border hover-lift">
            <h3 className="text-lg font-crimson font-semibold text-foreground mb-4">
              Backup & Sync
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-success/5 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-success rounded-full"></div>
                  <span className="text-sm font-inter text-foreground">All data synced</span>
                </div>
                <span className="text-xs font-inter text-muted-foreground">
                  {currentDate}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-lg font-crimson font-semibold text-primary">
                    {dashboardData?.syncStats?.notesCount}
                  </div>
                  <div className="text-xs font-inter text-muted-foreground">Notes Backed Up</div>
                </div>
                <div>
                  <div className="text-lg font-crimson font-semibold text-accent">
                    {dashboardData?.syncStats?.highlightsCount}
                  </div>
                  <div className="text-xs font-inter text-muted-foreground">Highlights Saved</div>
                </div>
              </div>
              
              <button className="w-full px-4 py-2 border border-primary text-primary rounded-lg font-inter font-medium text-sm hover:bg-primary hover:text-primary-foreground contemplative-transition">
                Manage Backup Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

PersonalDashboardInteractive.propTypes = {
  initialData: PropTypes?.shape({
    user: PropTypes?.shape({
      name: PropTypes?.string?.isRequired
    })?.isRequired,
    readingStats: PropTypes?.object?.isRequired,
    devotionalPlan: PropTypes?.object?.isRequired,
    prayerEntries: PropTypes?.array?.isRequired,
    notes: PropTypes?.array?.isRequired,
    goals: PropTypes?.array?.isRequired,
    achievements: PropTypes?.array?.isRequired,
    recommendations: PropTypes?.array?.isRequired,
    scheduledEvents: PropTypes?.array?.isRequired,
    bookmarks: PropTypes?.shape({
      audiobooks: PropTypes?.array?.isRequired,
      books: PropTypes?.array?.isRequired
    })?.isRequired,
    syncStats: PropTypes?.shape({
      notesCount: PropTypes?.number?.isRequired,
      highlightsCount: PropTypes?.number?.isRequired
    })?.isRequired
  })?.isRequired,
  user: PropTypes?.object,
  userProfile: PropTypes?.object,
  onDataUpdate: PropTypes?.func
};